import React, { useState, useRef, Dispatch, SetStateAction, useMemo } from 'react';
import { Box, Button, Stack, Tooltip, IconButton } from '@mui/material';
import { classes } from '../styles';
import DisplayHTMLViewOnly from '../../SharedComponents/DisplayHTMLViewOnly';
import { modalSteps } from '../types';
import { IWebsocketData } from '../../SharedComponents/types';
import { convertToLocalEnglish } from '../../../NewUI/Components/Utilities/convertToLocalEnglish';
import { handleAPIRequest } from '../../../NewUI/Components/Utilities/handleAPIRequest';
import { IJobApplication } from '../../../NewUI/Job/types';
import { StyledSnackbarProps } from '../../../NewUI/Components/CustomUIElements/StyledSnackbar';
import { LoadingOverlay } from '../../AIStudioModal/components/LoadingOverlay';
import {
  Done as DoneIcon,
  CachedOutlined as CachedIcon,
  FileCopy as FileCopyIcon
} from '@mui/icons-material';
import { classes as aiStudioClasses } from '../../AIStudioModal/styles';
import { handleRichCopy } from '../../../NewUI/Components/Utilities/handleRichCopy';
import { DynamicFooterWithInput } from '../../SharedComponents/Footers/DynamicFoorterWithInput';
import DynamicFooter from '../../SharedComponents/Footers/DynamicFooter';

interface IProps {
  jobId?: number;
  setStep?: Dispatch<SetStateAction<modalSteps>>;
  summaryData: IWebsocketData | null;
  requestInsights?: (feedbackInput?: string) => void;
  handleDismissNotification?: () => void;
  isNotification?: boolean;
  emails: string[];
  setEmails: Dispatch<SetStateAction<string[]>>;
  selectedApplicants: IJobApplication[];
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
  isLoading: boolean;
}

const SUGGESTED_PROMPTS = [
  'Make it more objective',
  'Balance strengths and weaknesses',
  'Focus more on resume',
  'Weigh screening questions more'
];

const ResponseStep = ({
  jobId,
  setStep,
  summaryData,
  requestInsights,
  handleDismissNotification,
  isNotification,
  emails,
  setEmails,
  selectedApplicants,
  setSnackbar,
  isLoading
}: IProps) => {
  const summaryRef = useRef(null);
  const [emailInputOpen, setEmailInputOpen] = useState<boolean>(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [copied, setCopied] = useState(false);
  const currentJobId = jobId || summaryData?.job_id;

  const downloadPDF = () => {
    const url = `/api/v4/jobs/${currentJobId}/bulk_application_summaries/${summaryData?.id}.pdf`;
    window.open(url, '_blank');
  };

  const handleSendEmailClick = () => {
    handleAPIRequest({
      url: `/api/v4/jobs/${currentJobId}/applications/send_ai_insights`,
      method: 'POST',
      body: {
        application_ids: selectedApplicants
          ? selectedApplicants.map((applicant) => applicant.id)
          : summaryData?.additional_information?.application_ids,
        summary_data: summaryData?.array_data,
        emails: emails.join(',')
      },
      onSuccess: () => {
        setEmailInputOpen(false);
        setEmails([]);
      },
      setLoaderState: setSendingEmails,
      setSnackbarState: setSnackbar
    });
  };

  const convertedSummaryData = useMemo(
    () =>
      convertToLocalEnglish(
        summaryData?.content || '<p>There was an error generating your summary</p>'
      ),
    [summaryData?.content]
  );

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={classes.responseHeader}>
        <Box sx={classes.header}>AI candidate insights</Box>
        {!isNotification && (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Tooltip placement="top" title={isNotification ? 'Close' : 'Regenerate'} arrow>
              <IconButton
                id={`${isNotification ? 'regenerate' : 'close'}-summary`}
                disabled={isLoading}
                onClick={isNotification ? handleDismissNotification : () => requestInsights?.('')}
                sx={aiStudioClasses.actionButton}
                size="small"
              >
                <CachedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title={`${copied ? 'Copied!' : 'Copy'}`} placement="top">
              <IconButton
                id="copy-tlp-html"
                sx={aiStudioClasses.actionButton}
                size="small"
                disabled={isLoading}
                onClick={() => handleRichCopy(summaryRef.current, setCopied)}
              >
                {copied ? <DoneIcon /> : <FileCopyIcon />}
              </IconButton>
            </Tooltip>

            {!isNotification && setStep && (
              <Button sx={aiStudioClasses.footerContinueButton} onClick={() => setStep('input')}>
                Back to settings
              </Button>
            )}
          </Stack>
        )}
      </Box>
      <Box sx={{ width: '100%', flex: 1, position: 'relative' }}>
        <DisplayHTMLViewOnly
          content={convertedSummaryData}
          viewerRef={summaryRef}
          onExportClick={downloadPDF}
          setEmails={setEmails}
          emails={emails}
          onSendEmailClick={handleSendEmailClick}
          emailInputOpen={emailInputOpen}
          setEmailInputOpen={setEmailInputOpen}
          sendingEmails={sendingEmails}
        />
        <LoadingOverlay isLoading={isLoading} generationType={'summary'} />
      </Box>
      {!isNotification ? (
        <DynamicFooterWithInput
          isLoading={isLoading}
          submitCallback={(feedbackInput) => requestInsights?.(feedbackInput)}
          description={
            <>
              Here are your <span>candidate insights</span>. Now let&apos;s go fill this role!
            </>
          }
          suggestedPrompts={SUGGESTED_PROMPTS}
          containerStyle={{
            marginTop: '36px'
          }}
        />
      ) : (
        <DynamicFooter
          primaryButtonText={copied ? 'Copied!' : 'Copy'}
          secondaryButtonText={isNotification ? 'Close' : 'Regenerate'}
          primaryButtonID="copy-summary-text"
          secondaryButtonID={`${isNotification ? 'regenerate' : 'close'}-summary`}
          primaryButtonMinWidth="110px"
          handlePrimaryButtonClick={() => handleRichCopy(summaryRef.current, setCopied)}
          handleSecondaryButtonClick={isNotification ? handleDismissNotification : requestInsights}
          felixMessage={
            <>
              Here are your <span>candidate insights</span>. Now let&apos;s go fill this role!
            </>
          }
          felixMessageMaxWidth="400px"
          alternateSecondaryButton={true}
          containerStyle={{
            marginTop: '18px'
          }}
        />
      )}
    </Box>
  );
};

export default ResponseStep;
